import type { CrawlIssue, CrawlPageResult, IssueSeverity, SitemapEntry } from "../../shared/types";
import { canonicalizeUrl, isSameOrigin } from "../../shared/url";

export interface DisplayIssue extends CrawlIssue {
  url: string;
  title?: string;
}

export function pageCanBeIndexed(page: CrawlPageResult, origin: string): boolean {
  if (page.blockedByRobots || !page.ok || page.status < 200 || page.status >= 300) {
    return false;
  }
  if (page.metaRobots?.includes("noindex")) {
    return false;
  }
  if (page.canonicalUrl && !isSameOrigin(page.canonicalUrl, origin)) {
    return false;
  }
  return true;
}

export function buildSitemapEntries(pages: CrawlPageResult[], origin: string): SitemapEntry[] {
  const entries = new Map<string, SitemapEntry>();
  for (const page of pages) {
    if (!pageCanBeIndexed(page, origin)) {
      continue;
    }

    const loc = page.canonicalUrl && isSameOrigin(page.canonicalUrl, origin)
      ? canonicalizeUrl(page.canonicalUrl)
      : canonicalizeUrl(page.finalUrl || page.normalizedUrl);

    if (!entries.has(loc)) {
      entries.set(loc, { loc, lastmod: page.lastModified });
    }
  }
  return [...entries.values()].sort((a, b) => a.loc.localeCompare(b.loc));
}

export function flattenIssues(pages: CrawlPageResult[]): DisplayIssue[] {
  return pages.flatMap((page) =>
    page.issues.map((issue) => ({
      ...issue,
      url: page.finalUrl || page.requestedUrl,
      title: page.title
    }))
  );
}

export function countBySeverity(issues: DisplayIssue[]): Record<IssueSeverity, number> {
  return issues.reduce<Record<IssueSeverity, number>>(
    (acc, issue) => {
      acc[issue.severity] += 1;
      return acc;
    },
    { error: 0, warning: 0, info: 0 }
  );
}

export function buildJsonReport(pages: CrawlPageResult[], entries: SitemapEntry[], issues: DisplayIssue[]): string {
  return JSON.stringify(
    {
      generatedAt: new Date().toISOString(),
      totals: {
        crawled: pages.length,
        sitemapUrls: entries.length,
        issues: issues.length
      },
      sitemap: entries,
      pages,
      issues
    },
    null,
    2
  );
}
